import { defineStore } from "pinia";
import { PortalEvent } from "./entity";
import { PortalEventList } from "./list.gen";
import type { PortalEventStore, PortalEventStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const portalEventList = new PortalEventList();
export const usePortalEventStore = defineStore("portalEventStore", {
  state: (): PortalEventStoreState => ({
    activeID: null,
    cache: {},
    list: [] as PortalEvent[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-PortalEvent", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.PortalEvent"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(portalEvent: PortalEvent): Promise<void> {
      // Set active
      await portalEvent
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = portalEvent.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(portalEvent: PortalEvent): void {
      const id = portalEvent.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(portalEvent);

        // Set active
        if (this.active === null) {
          this.activeID = portalEvent.id;
        }
      } else {
        this.reinsert(portalEvent);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as PortalEvent[]).map((x: any) => {
        const portalEvent = new PortalEvent({});
        portalEvent.set(x as JSONData);
        return portalEvent;
      });
    },
    async delete(portalEvent: PortalEvent): Promise<void> {
      await portalEvent
        .delete()
        .then(() => {
          this.remove(portalEvent);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const portalEvent = (this.list as PortalEvent[]).find(
        (x: PortalEvent) => x.id === id,
      );
      if (portalEvent !== undefined) {
        await this.delete(portalEvent);
      } else {
        throw new Error("Could not find PortalEvent with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as PortalEvent[]).some((x: PortalEvent) => x.id === id);
    },
    get(id: string): PortalEvent | null {
      return (
        (this.list as PortalEvent[]).find((x: PortalEvent) => x.id === id) ||
        null
      );
    },
    reinsert(portalEvent: PortalEvent): void {
      const index = (this.list as PortalEvent[]).findIndex(
        (x: PortalEvent) => x.id === portalEvent.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          portalEvent,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(portalEvent);
      }
      this.updateKey += 1;
    },
    remove({ id }: PortalEvent): void {
      const index = (this.list as PortalEvent[]).findIndex(
        (x: PortalEvent) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<PortalEvent> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const portalEvent = this.get(id);
        if (portalEvent !== null) {
          return portalEvent;
        }
      }

      // Retrieve portalEvent
      const portalEvent = new PortalEvent({});

      // Get portalEvent
      await portalEvent.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate portalEvent
        await this.activate(entity);
      });

      return portalEvent;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: PortalEvent[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as PortalEvent[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await portalEventList.getAll().then(async (list) => {
        list.forEach((portalEvent) => {
          this.reinsert(portalEvent);
        });

        // Activate portalEvent
        if (list.length > 0) {
          if (portalEventList.active !== null) {
            this.activeID = portalEventList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("portal_event_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as PortalEvent[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEvent[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PortalEvent),
        };
      }

      // Retrieve list
      const items = await portalEventList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEvent) => {
            this.reinsert(portalEvent);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEvent) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEvent[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PortalEvent),
        };
      }

      // Retrieve list
      const items = await portalEventList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEvent) => {
            this.reinsert(portalEvent);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEvent) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEvent[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as PortalEvent),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await portalEventList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEvent) => {
            this.reinsert(portalEvent);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEvent) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(portalEvent: PortalEvent): Promise<string> {
      // Update portalEvent
      await portalEvent.update();

      // Reinsert portalEvent
      this.reinsert(portalEvent);

      // Activate portalEvent
      if (this.active === null) {
        await this.activate(portalEvent);
      }

      this.emitStoreUpdateEvent();

      return portalEvent.id;
    },
  },
  getters: {
    active(): PortalEvent | null {
      if (this.activeID === null) {
        return null;
      }

      const portalEvent = (this.list as PortalEvent[]).find(
        (x: PortalEvent) => x.id === this.activeID,
      );
      if (portalEvent === undefined) {
        return null;
      }

      return portalEvent;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createPortalEventStore(): PortalEventStore {
  const store = usePortalEventStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PortalEventStore; // TODO: Fix this
}
